import React, { useState, useRef, useCallback } from 'react';

// Mini feather paths (viewBox 0 0 64 128)
const FEATHER_PATHS = {
  scarlett: "M32,4 C50,6 64,22 64,44 C64,66 52,88 40,104 C36,112 34,119 32,124 C30,119 28,112 24,104 C12,88 0,66 0,44 C0,22 14,6 32,4 Z",
  longtail: "M32,4 C40,6 44,18 44,36 C44,70 38,98 32,124 C26,98 20,70 20,36 C20,18 24,6 32,4 Z",
  camelot: "M32,4 C48,7 60,22 60,42 C60,62 48,84 36,102 C34,110 33,118 32,122 C31,118 30,110 28,102 C16,84 4,62 4,42 C4,22 16,7 32,4 Z",
  rainbow: "M32,4 C48,7 58,20 58,40 C58,62 46,86 34,106 C33,112 32,120 32,124 C32,120 31,112 30,106 C18,86 6,62 6,40 C6,20 16,7 32,4 Z",
  shamrock: "M32,4 C50,5 63,18 63,38 C64,58 52,82 40,100 C38,110 35,117 32,122 C29,117 26,110 24,100 C12,82 0,58 1,38 C2,18 14,5 32,4 Z",
  topknot: "M32,10 C32,10 24,8 20,4 C18,2 18,0 22,1 C26,3 30,7 32,10 C34,7 38,3 42,1 C46,0 46,2 44,4 C40,8 32,10 32,10 C50,12 62,24 62,46 C62,68 50,90 38,108 C35,114 33,119 32,122 C31,119 29,114 26,108 C14,90 2,68 2,46 C2,24 14,12 32,10 Z",
};

// Separate component for rainbow SVG
function RainbowFeatherSVG({ instanceId, bodyPath }) {
  return (
    <svg viewBox="0 0 64 128" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id={`mrg-${instanceId}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c62828"/>
          <stop offset="20%" stopColor="#e65100"/>
          <stop offset="40%" stopColor="#f9a825"/>
          <stop offset="60%" stopColor="#2e7d32"/>
          <stop offset="80%" stopColor="#1565c0"/>
          <stop offset="100%" stopColor="#6a1b9a"/>
        </linearGradient>
      </defs>
      <path
        d={bodyPath}
        fill={`url(#mrg-${instanceId})`}
        stroke="rgba(255,255,255,0.25)"
        strokeWidth="1.5"
      />
    </svg>
  );
}

// Separate component for solid color SVG
function SolidFeatherSVG({ color, bodyPath }) {
  return (
    <svg viewBox="0 0 64 128" style={{ width: '100%', height: '100%' }}>
      <path
        d={bodyPath}
        fill={color}
        stroke="rgba(255,255,255,0.25)"
        strokeWidth="1.5"
      />
    </svg>
  );
}

// Draggable feather component with rotation
function DraggableFeather({ feather, bodyPath, onUpdatePosition, onUpdateAngle, onRemove, containerRef }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, featherX: 0, featherY: 0, startAngle: 0 });
  const movedRef = useRef(false);

  // Calculate angle from center of feather to mouse position
  const getAngleFromCenter = useCallback((clientX, clientY, rect) => {
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;
    // Convert to degrees, with 0 at top
    let angle = Math.atan2(deltaX, -deltaY) * (180 / Math.PI);
    return (angle + 360) % 360;
  }, []);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    
    // If Shift is held OR right-click, start rotating
    if (e.shiftKey || e.button === 2) {
      setIsRotating(true);
      movedRef.current = true;
      dragStartRef.current = {
        startAngle: feather.angle || 0,
        mouseStartAngle: getAngleFromCenter(e.clientX, e.clientY, rect),
      };
    } else {
      setIsDragging(true);
      movedRef.current = false;
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        featherX: feather.x,
        featherY: feather.y,
      };
    }
    e.target.setPointerCapture(e.pointerId);
  }, [feather.x, feather.y, feather.angle, containerRef, getAngleFromCenter]);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging && !isRotating) return;
    e.preventDefault();
    
    if (isRotating) {
      const rect = e.currentTarget.getBoundingClientRect();
      const currentMouseAngle = getAngleFromCenter(e.clientX, e.clientY, rect);
      const angleDiff = currentMouseAngle - dragStartRef.current.mouseStartAngle;
      let newAngle = (dragStartRef.current.startAngle + angleDiff + 360) % 360;
      onUpdateAngle(feather.instanceId, Math.round(newAngle));
    } else if (isDragging) {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
      
      let newX = dragStartRef.current.featherX + dx;
      let newY = dragStartRef.current.featherY + dy;
      
      // Clamp to container bounds (280x180)
      newX = Math.max(10, Math.min(270, newX));
      newY = Math.max(10, Math.min(170, newY));
      
      onUpdatePosition(feather.instanceId, newX, newY);
    }
  }, [isDragging, isRotating, feather.instanceId, onUpdatePosition, onUpdateAngle, getAngleFromCenter]);

  const handlePointerUp = useCallback((e) => {
    // Tap (no drag) → rotate 30°
    if (!movedRef.current && isDragging) {
      const newAngle = ((feather.angle || 0) + 30) % 360;
      onUpdateAngle(feather.instanceId, newAngle);
    }
    setIsDragging(false);
    setIsRotating(false);
    e.target.releasePointerCapture(e.pointerId);
  }, [isDragging, feather.angle, feather.instanceId, onUpdateAngle]);

  // Scroll to rotate (alternative method)
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? 10 : -10;
    const newAngle = ((feather.angle || 0) + delta + 360) % 360;
    onUpdateAngle(feather.instanceId, newAngle);
  }, [feather.angle, feather.instanceId, onUpdateAngle]);

  // Prevent context menu on right-click (we use it for rotation)
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
  }, []);

  // Double-click to remove
  const handleDoubleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove(feather.instanceId);
  }, [feather.instanceId, onRemove]);

  const isRainbow = feather.type === 'rainbow';

  return (
    <div
      data-testid={`placed-feather-${feather.instanceId}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onWheel={handleWheel}
      onContextMenu={handleContextMenu}
      onDoubleClick={handleDoubleClick}
      style={{
        position: 'absolute',
        left: `${feather.x}px`,
        top: `${feather.y}px`,
        width: '28px',
        height: '56px',
        transform: `translate(-50%, -50%) rotate(${feather.angle || 0}deg)`,
        cursor: isRotating ? 'crosshair' : (isDragging ? 'grabbing' : 'grab'),
        touchAction: 'none',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
        transition: (isDragging || isRotating) ? 'none' : 'filter 0.15s ease',
        zIndex: (isDragging || isRotating) ? 100 : 10,
      }}
      title="Drag to move • Shift+drag or right-drag to rotate • Scroll to rotate • Double-click to remove"
    >
      {/* Rotation indicator ring - visible on hover */}
      <div style={{
        position: 'absolute',
        inset: '-8px',
        border: '1px dashed rgba(255,215,0,0.3)',
        borderRadius: '50%',
        opacity: (isDragging || isRotating) ? 0.8 : 0,
        transition: 'opacity 0.2s',
        pointerEvents: 'none',
      }}/>
      
      {isRainbow ? (
        <RainbowFeatherSVG instanceId={feather.instanceId} bodyPath={bodyPath} />
      ) : (
        <SolidFeatherSVG color={feather.color} bodyPath={bodyPath} />
      )}
    </div>
  );
}

export default function GoldMask({ placedFeathers, isGlowing, onRemoveFeather, onUpdatePosition, onUpdateAngle, onComplete, canComplete }) {
  const containerRef = useRef(null);

  return (
    <div
      ref={containerRef}
      data-testid="gold-mask"
      style={{ position: 'relative', width: '280px', height: '180px', overflow: 'visible' }}
    >
      {/* Glow halo */}
      {isGlowing && (
        <div style={{
          position: 'absolute',
          inset: '-28px',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(255,215,0,0.22) 0%, transparent 70%)',
          animation: 'maskHaloGlow 2.8s ease-in-out infinite',
          pointerEvents: 'none',
          zIndex: 0,
        }}/>
      )}

      {/* Mask SVG */}
      <svg
        viewBox="0 0 240 150"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          width: '100%',
          height: '100%',
          imageRendering: 'pixelated',
          filter: isGlowing
            ? undefined
            : 'drop-shadow(0 5px 14px rgba(0,0,0,0.75)) drop-shadow(0 0 5px rgba(255,215,0,0.18))',
          animation: isGlowing ? 'maskGlowPulse 2.8s ease-in-out infinite' : 'none',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <defs>
          <linearGradient id="mgb" x1="0.2" y1="0" x2="0.8" y2="1">
            <stop offset="0%" stopColor="#ffe57a"/>
            <stop offset="32%" stopColor="#ffd700"/>
            <stop offset="66%" stopColor="#daa520"/>
            <stop offset="100%" stopColor="#b8860b"/>
          </linearGradient>
          <radialGradient id="mgs" cx="0.38" cy="0.26" r="0.52">
            <stop offset="0%" stopColor="rgba(255,255,255,0.32)"/>
            <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
          </radialGradient>
          <linearGradient id="mge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#cfa028"/>
            <stop offset="100%" stopColor="#7a5008"/>
          </linearGradient>
        </defs>

        {/* Face body */}
        <path
          d="M22,70 C22,30 50,8 80,8 C97,8 108,20 120,32
             C132,20 143,8 160,8 C190,8 218,30 218,70
             C218,98 196,124 168,132 C154,137 136,139 120,139
             C104,139 86,137 72,132 C44,124 22,98 22,70 Z"
          fill="url(#mgb)"
          stroke="url(#mge)"
          strokeWidth="1.5"
        />

        {/* Left eye hole */}
        <ellipse cx="84" cy="66" rx="26" ry="20" fill="#050204"/>
        {/* Right eye hole */}
        <ellipse cx="156" cy="66" rx="26" ry="20" fill="#050204"/>

        {/* Eye golden rings */}
        <ellipse cx="84" cy="66" rx="26" ry="20" fill="none" stroke="#daa520" strokeWidth="1"/>
        <ellipse cx="156" cy="66" rx="26" ry="20" fill="none" stroke="#daa520" strokeWidth="1"/>

        {/* Nose bridge */}
        <path
          d="M114,88 C112,98 113,108 120,112 C127,108 128,98 126,88"
          fill="rgba(130,90,0,0.12)"
          stroke="#b8860b"
          strokeWidth="0.9"
        />

        {/* Top flourish */}
        <path
          d="M113,8 C111,2 108,0 108,0 C110,3 112,6 113,8
             M120,8 C120,1 120,-3 120,-3 C120,-3 120,1 120,8
             M127,8 C128,6 130,3 132,0 C132,0 129,2 127,8"
          fill="#b8860b"
          stroke="#9a7010"
          strokeWidth="0.6"
        />

        {/* Decorative dashed edge accents */}
        <path d="M50,35 C62,20 72,14 80,11" stroke="rgba(218,165,32,0.38)" strokeWidth="1" fill="none" strokeDasharray="2,3"/>
        <path d="M190,35 C178,20 168,14 160,11" stroke="rgba(218,165,32,0.38)" strokeWidth="1" fill="none" strokeDasharray="2,3"/>

        {/* Shine overlay */}
        <path
          d="M22,70 C22,30 50,8 80,8 C97,8 108,20 120,32
             C132,20 143,8 160,8 C190,8 218,30 218,70
             C218,98 196,124 168,132 C154,137 136,139 120,139
             C104,139 86,137 72,132 C44,124 22,98 22,70 Z"
          fill="url(#mgs)"
        />
      </svg>

      {/* Placed feathers - draggable */}
      {placedFeathers.map((feather) => {
        const bodyPath = FEATHER_PATHS[feather.type] || FEATHER_PATHS.rainbow;
        return (
          <DraggableFeather
            key={feather.instanceId}
            feather={feather}
            bodyPath={bodyPath}
            onUpdatePosition={onUpdatePosition}
            onUpdateAngle={onUpdateAngle}
            onRemove={onRemoveFeather}
            containerRef={containerRef}
          />
        );
      })}

      {/* Complete button - appears when feathers are placed */}
      {canComplete && placedFeathers.length > 0 && (
        <button
          data-testid="complete-mask-btn"
          onClick={onComplete}
          style={{
            position: 'absolute',
            bottom: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(255, 215, 0, 0.15)',
            border: '1px solid rgba(255, 215, 0, 0.4)',
            borderRadius: '4px',
            padding: '8px 20px',
            color: 'rgba(255, 215, 0, 0.8)',
            cursor: 'pointer',
            fontFamily: "'Caveat', cursive",
            fontSize: '14px',
            letterSpacing: '0.1em',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 215, 0, 0.25)';
            e.target.style.borderColor = 'rgba(255, 215, 0, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 215, 0, 0.15)';
            e.target.style.borderColor = 'rgba(255, 215, 0, 0.4)';
          }}
        >
          Complete the Mask
        </button>
      )}

      {/* Instructions hint */}
      {placedFeathers.length > 0 && placedFeathers.length < 3 && (
        <div
          style={{
            position: 'absolute',
            bottom: '-70px',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255, 215, 0, 0.4)',
            fontSize: '12px',
            fontFamily: "'Caveat', cursive",
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          tap to rotate • drag to move • double-click to remove
        </div>
      )}
    </div>
  );
}
